import { useCallback, useMemo } from 'react';
import { UseTableInstanceProps, UsePaginationInstanceProps } from 'react-table';
import { VariableSizeList } from 'react-window';
import {
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    Flex,
    FlexProps,
    Center,
    Text,
    Icon,
    TableHeadProps,
    Spinner,
} from '@chakra-ui/react';
import icons from 'shared/icons';
import { getScrollbarWidth } from './helpers/scrollBar';
import GlobalFilter from './filters/GlobalFilter';
import { TSortable } from 'types';
import { Row } from 'react-table';

export interface ICustomFilter {
    id: string;
    value: string;
    onChange: (val: string) => void;
    onSubmit?: () => void;
}
export interface TableNewProps<T extends object>
    extends UseTableInstanceProps<T>,
        Partial<UsePaginationInstanceProps<T>> {
    containerProps?: FlexProps;
    isVirtualised?: boolean;
    isPagination?: boolean;
    isHeaderFixed?: boolean;
    customFilters?: ICustomFilter[];
    manualSort?: boolean;
    sort?: string;
    sortDirection?: TSortable;
    sortableColumns?: {
        [key: string]: TSortable;
    };
    handleSort?: (columnId: string | undefined) => void;
    isLoading?: boolean;
}

export function DITable<T extends object>(props: TableNewProps<T>) {
    const {
        getTableProps,
        headerGroups,
        getTableBodyProps,
        rows,
        prepareRow,
        containerProps,
        isVirtualised,
        totalColumnsWidth,
        page,
        isPagination = false,
        isHeaderFixed = false,
        customFilters,
        manualSort,
        sortableColumns,
        handleSort,
        isLoading,
    } = props;

    const scrollBarSize = useMemo(() => getScrollbarWidth(), []);
    const renderRow = useCallback(
        ({ index, style }: { index: number; style: any }) => {
            const row = rows[index];
            prepareRow(row);
            return (
                <Tr
                    borderBottom={'1px solid'}
                    borderColor="gray.100"
                    _last={{ borderBottom: 'none' }}
                    {...row.getRowProps({
                        style,
                    })}
                >
                    {row.cells.map((cell) => {
                        return (
                            <Td py="3" {...cell.getCellProps()}>
                                {cell.render('Cell')}
                            </Td>
                        );
                    })}
                </Tr>
            );
        },
        [prepareRow, rows],
    );

    const renderRecursiveRows = (rows: Row<any>[]): JSX.Element[] => {
        return rows.map((row) => {
            prepareRow(row);
            return (
                <>
                    <Tr
                        borderBottom={'1px solid'}
                        borderColor="gray.100"
                        fontSize="sm"
                        _last={{ borderBottom: 'none' }}
                        {...row.getRowProps()}
                    >
                        {row.cells.map((cell) => {
                            return (
                                <Td py="3" {...cell.getCellProps()}>
                                    {cell.render('Cell')}
                                </Td>
                            );
                        })}
                    </Tr>

                    {row.original.isExpanded && row.subRows && renderRecursiveRows(row.subRows)}
                </>
            );
        });
    };

    const renderRows = () => {
        const neededRows = isPagination ? page : rows;

        const renderedRows = neededRows && renderRecursiveRows(neededRows);
        if (isVirtualised) {
            return (
                <VariableSizeList
                    height={678}
                    itemCount={rows.length}
                    itemSize={() => 80}
                    width={totalColumnsWidth + scrollBarSize}
                >
                    {renderRow}
                </VariableSizeList>
            );
        }

        return renderedRows;
    };

    const renderEmptyRows = () => {
        return (
            <Tr>
                <Td colSpan={headerGroups[0].headers.length}>
                    <Center>
                        <Text fontSize="sm">No rows to show</Text>
                    </Center>
                </Td>
            </Tr>
        );
    };

    const renderColumnFilter = (columnHeader: string, filters: ICustomFilter[]) => {
        const filter = filters.find((f) => f.id === columnHeader);
        if (filter) {
            return (
                <Flex alignItems="center">
                    <GlobalFilter
                        globalFilter={filter.value}
                        setGlobalFilter={filter.onChange}
                        submitSearch={filter.onSubmit}
                        inputGroupStyles={{ width: '100%' }}
                    />
                </Flex>
            );
        }
    };

    const containerStyle = containerProps || {};
    const SortDescIcon = icons.sortDown;
    const SortAsc = icons.sortUp;
    const SortIcon = icons.sort;
    const headerFixedProps: TableHeadProps = isHeaderFixed
        ? { position: 'sticky', bg: 'white', top: 0, zIndex: 'sticky', boxShadow: 'base' }
        : { borderBottom: '1px solid', borderColor: 'gray.100' };

    return (
        <Flex
            maxH="640px"
            overflow="auto"
            border="1px solid"
            borderColor="gray.100"
            {...containerStyle}
            direction="column"
        >
            <Table {...getTableProps()} bg={'white'}>
                <Thead {...headerFixedProps}>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => {
                                const sortIcons = {
                                    asc: SortAsc,
                                    desc: SortDescIcon,
                                    default: SortIcon,
                                };

                                const SortableColumnIcon = ({ columnId }: { columnId: string }) => {
                                    if (!sortableColumns || !sortableColumns[columnId]) {
                                        return null;
                                    }

                                    const IconType = sortIcons[sortableColumns[columnId]];

                                    return <Icon ml="8px" color="gray.400" w={4} h={4} as={IconType} />;
                                };

                                return (
                                    <Th
                                        {...column.getHeaderProps({
                                            style: {
                                                whiteSpace: 'nowrap',
                                                minWidth: column.minWidth,
                                                width: column.width,
                                                height: '100%',
                                            },
                                        })}
                                    >
                                        <Flex h={'100%'} justifyContent="space-between" flexDirection="column">
                                            <Flex
                                                w="100%"
                                                alignItems="center"
                                                {...(column.canFilter || manualSort ? { mb: 2 } : {})}
                                                cursor={column.canSort ? 'pointer' : 'auto'}
                                                {...(manualSort && column.canSort
                                                    ? {
                                                          onClick: () => {
                                                              if (handleSort) handleSort(column.id);
                                                          },
                                                      }
                                                    : column.canSort
                                                    ? column.getSortByToggleProps()
                                                    : {})}
                                            >
                                                {column.render('Header')}
                                                {manualSort && column.canSort ? (
                                                    <SortableColumnIcon columnId={column.id} />
                                                ) : (
                                                    <>
                                                        {column.canSort && !column.isSorted && (
                                                            <Icon ml="8px" color="gray.400" w={4} h={4} as={SortIcon} />
                                                        )}
                                                        {column.isSorted &&
                                                            (column.isSortedDesc ? (
                                                                <Icon
                                                                    ml="8px"
                                                                    color="gray.400"
                                                                    w={4}
                                                                    h={4}
                                                                    as={SortDescIcon}
                                                                />
                                                            ) : (
                                                                <Icon
                                                                    ml="8px"
                                                                    color="gray.400"
                                                                    w={4}
                                                                    h={4}
                                                                    as={SortAsc}
                                                                />
                                                            ))}
                                                    </>
                                                )}
                                            </Flex>

                                            {customFilters
                                                ? renderColumnFilter(column.id, customFilters)
                                                : column.canFilter && <Flex>{column.render('Filter')}</Flex>}
                                        </Flex>
                                    </Th>
                                );
                            })}
                        </Tr>
                    ))}
                </Thead>
                <Tbody color="gray.700" {...getTableBodyProps()}>
                    {!isLoading && <>{rows.length > 0 ? renderRows() : renderEmptyRows()}</>}
                </Tbody>
            </Table>
            {isLoading && (
                <Center w="100%" h="50px" bg="white">
                    <Spinner />
                </Center>
            )}
        </Flex>
    );
}
